/**
 * Single datetime panel — calendar → Select time → Confirm (same flow as GreenTimeRangePicker).
 */
import React, { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { CalendarPicker } from "react-minimal-datetime-range";
import LayerdTimeColumns from "./LayerdTimeColumns";
import {
  LAYERD_SINGLE_DATETIME_PICKER_CLASS,
  layerdSingleDateTimePickerSx,
} from "./styles";
import {
  isDateBeforeToday,
  isDateTimeLocalBeforeNow,
  pickerValueToDateTimeLocal,
  splitDateTimeLocal,
  todayDateString,
} from "./utils";

const PAST_DATE_ERROR = "Date and time must be in the future.";

function dateFromPick(res, fallback) {
  if (typeof res === "string" && res) return res;
  if (res?.value) return res.value;
  const day = res?.date ?? res?.name;
  if (res?.year != null && res?.month != null && day != null) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${res.year}-${pad(res.month)}-${pad(day)}`;
  }
  return fallback;
}

export default function SingleDateTimePickerPanel({ value, onConfirm, disablePast = false }) {
  const initial = splitDateTimeLocal(value);
  const [pickedDate, setPickedDate] = useState(initial.date);
  const [hour, setHour] = useState(initial.time.split(":")[0]);
  const [minute, setMinute] = useState(initial.time.split(":")[1]);
  const [step, setStep] = useState("date");
  const [dateSelected, setDateSelected] = useState(Boolean(value));
  const [calendarJumpKey, setCalendarJumpKey] = useState(0);
  const [confirmError, setConfirmError] = useState("");

  const handleDatePicked = useCallback((res) => {
    const nextDate = dateFromPick(res, pickedDate);
    if (disablePast && isDateBeforeToday(nextDate)) {
      setConfirmError(PAST_DATE_ERROR);
      return;
    }
    setConfirmError("");
    setPickedDate(nextDate);
    setDateSelected(true);
  }, [disablePast, pickedDate]);

  const toggleStep = () => {
    if (!dateSelected) return;
    setStep((s) => (s === "date" ? "time" : "date"));
  };

  const handleConfirm = () => {
    if (!dateSelected) return;
    const next = pickerValueToDateTimeLocal(`${pickedDate} ${hour}:${minute}`);
    if (disablePast && isDateTimeLocalBeforeNow(next)) {
      setConfirmError(PAST_DATE_ERROR);
      return;
    }
    setConfirmError("");
    onConfirm(next);
  };

  const handleToday = () => {
    setPickedDate(todayDateString());
    setDateSelected(true);
    setCalendarJumpKey((k) => k + 1);
    setConfirmError("");
  };

  return (
    <Box className={LAYERD_SINGLE_DATETIME_PICKER_CLASS} sx={layerdSingleDateTimePickerSx}>
      <div className="react-minimal-datetime-range visible">
        <div className="react-minimal-datetime-date-piker">
          {step === "date" ? (
            <div className="react-minimal-datetime-range__calendar">
              <CalendarPicker
                key={`calendar-${calendarJumpKey}`}
                show={true}
                locale="en-us"
                allowPageClickToClose={false}
                defaultDate={pickedDate}
                defaultTimes={[`${hour}:${minute}`]}
                markedDates={[todayDateString()]}
                supportDateRange={disablePast ? [todayDateString(), "2099-12-31"] : undefined}
                onDatePicked={handleDatePicked}
                style={{ border: "none", padding: 0, background: "transparent" }}
              />
            </div>
          ) : (
            <div className="react-minimal-datetime-range__time-piker layerd-single-datetime-picker__time">
              <LayerdTimeColumns
                dateLabel={pickedDate}
                hour={hour}
                minute={minute}
                onHour={setHour}
                onMinute={setMinute}
              />
            </div>
          )}
        </div>
        {confirmError ? (
          <div className="layerd-single-datetime-picker__error">{confirmError}</div>
        ) : null}
        <div className="layerd-single-datetime-picker__footer">
          {step === "date" ? (
            <button
              type="button"
              className="layerd-single-datetime-picker__today"
              onClick={handleToday}
            >
              Today
            </button>
          ) : (
            <span className="layerd-single-datetime-picker__footer-spacer" />
          )}
          <div className="layerd-single-datetime-picker__footer-actions">
            <div
              className={`react-minimal-datetime-range__button react-minimal-datetime-range__button--type${
                dateSelected ? "" : " disabled"
              }`}
              onClick={dateSelected ? toggleStep : undefined}
            >
              {step === "date" ? "Select time" : "Select date"}
            </div>
            <div
              className={`react-minimal-datetime-range__button react-minimal-datetime-range__button--confirm${
                dateSelected ? "" : " disabled"
              }`}
              onClick={dateSelected ? handleConfirm : undefined}
            >
              Confirm
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
}
